import {eq} from '@shared/helpers/lang'
import {mapValueKeys} from '@shared/helpers/object'

import {selectCardPromotionGroup} from '@web/actions/cardPromotion'
import {
    getIntegratedDiscount,
    filterProductCoupon,
    getDiscountInfo,
    getOptimizedCoupon,
    getDeliveryFeeCoupons,
    getProductCoupons,
    validateSelectedCoupon,
    getCouponPageType,
    getCouponListChanged,
    getReloadStatus,
} from '@web/actions/orderSheetDiscount'
import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {APIStatus} from '@web/core/constants'
import {OrderSheetCoupon, OrderSheetImmediateCoupon} from '@web/slices/orderSheetCoupon/slice'
import makeSlice from '@web/slices/orderSheetCoupon/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByStorage.fulfilled, (state, {payload}) => {
                const {
                    data: {orderSheetCoupon},
                } = payload

                if (eq(state.couponStatus, 'default')) {
                    // 쿠폰 복원시 보유한 쿠폰 내에서만 복원 시도
                    state.selected.immediateCoupon = {
                        ...filterProductCoupon<OrderSheetImmediateCoupon, OrderSheetImmediateCoupon>({
                            selectedCoupons: orderSheetCoupon.selected.immediateCoupon,
                            couponsByOrderSheetItemId: state.product.immediateCouponsByOrderSheetItemId,
                            someFunction: ({policyNo}, coupon) => policyNo === coupon.policyNo,
                        }),
                    }
                    state.selected.productCoupon = {
                        ...filterProductCoupon<OrderSheetCoupon, OrderSheetCoupon>({
                            selectedCoupons: orderSheetCoupon.selected.productCoupon,
                            couponsByOrderSheetItemId: state.product.couponsByOrderSheetItemId,
                            someFunction: ({couponId}, coupon) => couponId === coupon.couponId,
                        }),
                    }
                    state.selected.duplicateCoupon = {
                        ...filterProductCoupon<OrderSheetCoupon, OrderSheetCoupon>({
                            selectedCoupons: orderSheetCoupon.selected.duplicateCoupon,
                            couponsByOrderSheetItemId: state.product.duplicateCouponsByOrderSheetItemId,
                            someFunction: ({couponId}, coupon) => couponId === coupon.couponId,
                        }),
                    }
                    // 첫주문일경우 배송지를 등록하고, 배송쿠폰을 복원하면, 쿠폰이 없다가 생겨난다.
                    // 때문에 배송 쿠폰은 그대로 복원하고, 검증 여부를 selector 에서 처리한다.
                    state.selected.deliveryCoupon = orderSheetCoupon.selected.deliveryCoupon
                    state.selected.storeCoupon = {
                        ...mapValueKeys((coupon, merchantNo) => {
                            return coupon &&
                                state.product.couponsByMerchantNo[merchantNo]?.some(
                                    ({couponId}) => couponId === coupon.couponId,
                                )
                                ? coupon
                                : null
                        }, orderSheetCoupon.selected.storeCoupon),
                    }
                    state.selected.multiPurchaseCoupon = orderSheetCoupon.selected.multiPurchaseCoupon
                    state.beforeApplyTemporaryCoupon = orderSheetCoupon.beforeApplyCoupon
                }
            })
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {
                    data: {coupon, couponFromMyOptimalDiscount},
                } = payload

                state.couponStatus = coupon.fallback ? 'failed' : 'default'
                state.discountOptimization = coupon.discountOptimization

                state.deliveryFee = getDeliveryFeeCoupons(coupon)
                state.product = getProductCoupons(coupon)

                const optimizedCoupon = getOptimizedCoupon(coupon)
                state.optimizedCoupon = optimizedCoupon
                state.selected = optimizedCoupon
                state.discount = getDiscountInfo(coupon)

                // 나의할인가 API가 성공했고, 나의할인가 쿠폰조합이 있는 경우
                const hasMyOptimizedInfo = coupon.discountOptimization && couponFromMyOptimalDiscount
                state.myOptimizedInfo = hasMyOptimizedInfo
                    ? {
                          optimizedCoupon: getOptimizedCoupon(couponFromMyOptimalDiscount),
                          discountInfo: getDiscountInfo(couponFromMyOptimalDiscount),
                      }
                    : undefined
            })

            .addCase(getIntegratedDiscount.fulfilled, (state, {payload}) => {
                // 통합할인조회는 기본적으로 선택된 쿠폰을 제외한 정보를 전부 업데이트 시킨다.
                const {from, data, shouldSelectedUpdate, myOptimalDiscountDiff} = payload
                const {coupon, couponFromMyOptimalDiscount} = data

                state.apiStatus = APIStatus.FETCHING
                state.couponStatus = coupon.fallback ? 'failed' : 'default'
                state.discountOptimization = coupon.discountOptimization

                const deliveryFee = getDeliveryFeeCoupons(coupon)
                const product = getProductCoupons(coupon)

                // 내려받은 쿠폰리스트가 변경 감지
                const isCouponListChanged = getCouponListChanged({
                    prev: {
                        deliveryFee: state.deliveryFee,
                        product: state.product,
                    },
                    curr: {
                        deliveryFee,
                        product,
                    },
                })

                // 쿠폰 리스트 업데이트
                state.deliveryFee = deliveryFee
                state.product = product

                // 내려받은 주문할인가 쿠폰 조합 기준으로 선택된 쿠폰조합 제외 전부 반영
                const optimizedCoupon = getOptimizedCoupon(coupon)

                state.optimizedCoupon = optimizedCoupon
                state.beforeApplyCoupon = optimizedCoupon
                state.beforeApplyTemporaryCoupon = optimizedCoupon

                // 나의할인가 API가 성공했고, 나의할인가 쿠폰조합이 있는 경우
                const hasMyOptimizedInfo = coupon.discountOptimization && couponFromMyOptimalDiscount
                state.myOptimizedInfo = hasMyOptimizedInfo
                    ? {
                          optimizedCoupon: getOptimizedCoupon(couponFromMyOptimalDiscount),
                          discountInfo: getDiscountInfo(couponFromMyOptimalDiscount),
                      }
                    : undefined

                /**
                 * 선택된 쿠폰 반영해야하는 경우
                 * - 배송비 변경된 경우
                 * - 쿠폰페이지에서 통합할인 조회 실패 한 경우
                 */
                if (shouldSelectedUpdate) {
                    state.selected = optimizedCoupon
                }
                state.reloadStatus = getReloadStatus({
                    from,
                    reloadStatus: state.reloadStatus,
                })

                const {isValid, selected} = validateSelectedCoupon({
                    product: state.product,
                    deliveryFee: state.deliveryFee,
                    selected: state.selected,
                })

                // 쿠폰 모달 case
                if (from === 'COUPON_RELOAD' && !shouldSelectedUpdate) {
                    state.couponPageType = getCouponPageType({
                        isBenefitCouponInquiryFailed: coupon.fallback,
                        isSelectedCouponDeleted: !isValid,
                        isCouponListChanged,
                        isMyOptimalDiscountChanged: myOptimalDiscountDiff.changed,
                    })
                }
                // 복원 시
                if (from === 'RESTORE') {
                    // 선택된 쿠폰이 없어졌다면 반영
                    state.selected = selected
                    // 선택된 쿠폰이 없어진 경우 선택된 쿠폰이 제거된다면 쿠폰 할인 금액또한 변경되어야 함
                    state.beforeApplyTemporaryCoupon = selected
                }

                // 카드프로모션 리로드
                if (from === 'CARD_PROMOTION_RELOAD') {
                    // 선택된 쿠폰이 없어졌거나 나의할인가가 변경된 경우
                    if (!isValid || myOptimalDiscountDiff.isCouponChanged) {
                        state.couponPageType = 'changed'
                    }
                    // 선택된 쿠폰이 없어졌다면 반영
                    state.selected = selected
                    // 선택된 쿠폰이 없어진 경우 선택된 쿠폰이 제거된다면 쿠폰 할인 금액또한 변경되어야 함
                    state.beforeApplyTemporaryCoupon = selected
                }
            })
            .addCase(selectCardPromotionGroup.fulfilled, (state, {payload}) => {
                // 나의할인가인 프로모션 정책 선택한 경우 선택된 쿠폰조합을 나의할인가 조합으로 변경한다.
                if (payload.isMyDiscount && state.myOptimizedInfo) {
                    state.selected = state.myOptimizedInfo?.optimizedCoupon
                    state.discount = state.myOptimizedInfo.discountInfo
                    /**
                     * 나할 선택의 경우 할인금액 재조회를 하지 않음
                     * 할인금액을 이미 알기 때문에 재조회하지 않아도 됨
                     * 재조회 상태를 제거한다.
                     */
                    state.reloadStatus = undefined
                }
            })
    },
})

export default slice.reducer
